import { HttpError, useList, useOne } from "@refinedev/core";
import Select from "components/select";
import { useEffect, useState } from "react";
import { TShirtSizeTable } from "./table";
import Pagination from "components/pagination";
import { IDistanceIdsItem } from "interfaces";
import { TShirtSizeCard } from "./card";

const ALL_OPTION = {label: "All", value: ""};

const GENDER_LIST = [
  {...ALL_OPTION},
  {label: "Male", value: "Nam/ Male"},
  {label: "Female", value: "Nữ/ Female"}
];

export const TShirtSizeInfo = ({currentFilterValues}: {currentFilterValues: any}) => {
  const [tShirtSizeFilters, setTShirtSizeFilters] = useState({
    t_shirt_size: "",
    gender: "",
    category: "",
    page: "1"
  });

  const {
    data,
    refetch,
    isLoading
  } = useList<any>({
    resource: `dashboard/event/${currentFilterValues.event_id}/t-shirt-size`,
    pagination: {
      mode: "off"
    },
    meta: {
      resource: "data",
      filters: [
        { field: "from_date", operator: "eq", value: currentFilterValues.from_date },
        { field: "to_date", operator: "eq", value: currentFilterValues.to_date },
        { field: "ticket_platform", operator: "eq", value: currentFilterValues.ticket_platform },

        { field: "t_shirt_size", operator: "eq", value: tShirtSizeFilters.t_shirt_size },
        { field: "gender", operator: "eq", value: tShirtSizeFilters.gender },
        { field: "category", operator: "eq", value: tShirtSizeFilters.category },
        { field: "page", operator: "eq", value: tShirtSizeFilters.page },
      ],
    },
    queryOptions: {
      enabled: false
    } as any
  });

  const { data: distanceData, isLoading: isLoadingDistance } = useOne<IDistanceIdsItem[], HttpError>({
    resource: "dashboard/event",
    id: `${currentFilterValues?.event_id}/distances`,
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!(currentFilterValues.event_id),
    } as any,
  });

  const { data: tShirtSizeData, isLoading: isLoadingTshirtSize } = useOne<{t_shirt_size: string}[], HttpError>({
    resource: "dashboard/event",
    id: `${currentFilterValues?.event_id}/t-shirt-sizes`,
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!(currentFilterValues.event_id),
    } as any,
  });

  const distanceDataIds = ((distanceData?.data ?? []).length > 0 ? [ALL_OPTION] : []).concat((distanceData?.data ?? []).map((distance: IDistanceIdsItem) => (
    { value: distance.category_name_en, label: distance.category_name_en }
  )));

  const tShirtSizeDataIds = ((tShirtSizeData?.data ?? []).length > 0 ? [ALL_OPTION] : []).concat((tShirtSizeData?.data ?? []).map((size: {t_shirt_size: string}) => (
    { value: size.t_shirt_size, label: size.t_shirt_size }
  )));

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFilterValues, tShirtSizeFilters]);

  useEffect(() => {
    setTShirtSizeFilters({
      t_shirt_size: "",
      gender: "",
      category: "",
      page: "1"  
    })
  }, [currentFilterValues]); // reset state on filter props change

  const _handleFilterChange = (key: string, value: any) => {
    const updatedFilter = {
      [key]: value,
      ...(key !== "page" && {page: "1"})
    }
    setTShirtSizeFilters({
      ...tShirtSizeFilters,
      ...updatedFilter
    })
  }

  const tableQueryResult = data;

  return (
    <div className="box-container">
      <div className="flex flex-col gap-y-4 lg:flex-row gap-x-2 md:gap-x-3 mb-4">
        <div className="flex-1">
          <label htmlFor="distance" className="block text-sm font-medium leading-6">
            Distance
          </label>
          <div className="mt-2">
            <Select 
              key={`distance__${currentFilterValues.event_id}`}
              options={distanceDataIds}
              id="distance"
              isLoading={isLoadingDistance}
              placeholder="Select Distance"
              className="py-0.5 w-full sm:max-w-full !rounded-md"
              value={distanceDataIds.find((selected: {label: string; value: string}) => selected.value === (tShirtSizeFilters.category)) || ""}
              onChange={(selected: {label: string; value: string}) => _handleFilterChange("category", selected.value)}      
            />
          </div>
        </div>
        <div className="flex-1">
          <label htmlFor="gender" className="block text-sm font-medium leading-6">
            Gender
          </label>
          <div className="mt-2">
            <Select 
              key={`gender__${currentFilterValues.event_id}`}
              options={GENDER_LIST}
              id="gender"
              placeholder="Select Gender"
              className="py-0.5 w-full sm:max-w-full !rounded-md"
              value={GENDER_LIST.find((selected: {label: string; value: string}) => selected.value === (tShirtSizeFilters.gender)) || ""}
              onChange={(selected: {label: string; value: string}) => _handleFilterChange("gender", selected.value)}      
            />
          </div>
        </div>
        <div className="flex-1">
          <label htmlFor="t_shirt_size" className="block text-sm font-medium leading-6">
            Tshirt size
          </label>
          <div className="mt-2">
            <Select 
              key={`t_shirt_size__${currentFilterValues.event_id}`}
              options={tShirtSizeDataIds}
              isLoading={isLoadingTshirtSize}
              id={`t_shirt_size__${currentFilterValues.event_id}`}
              placeholder="Select Size"
              className="py-0.5 w-full sm:max-w-full !rounded-md"
              value={tShirtSizeDataIds.find((selected: {label: string; value: string}) => selected.value === (tShirtSizeFilters.t_shirt_size)) || ""}
              onChange={(selected: {label: string; value: string}) => _handleFilterChange("t_shirt_size", selected.value)}      
            />
          </div>
        </div>
      </div>
      <div className="sm:hidden">
        <TShirtSizeCard 
          data={tableQueryResult?.data}
          pageSize={tableQueryResult?.pageSize}
          currentPage={tableQueryResult?.paging.current_page}
          isLoading={isLoading}
        />
      </div>
      <div className="hidden sm:block">
        <TShirtSizeTable
          data={tableQueryResult?.data}
          pageSize={tableQueryResult?.pageSize}
          currentPage={tableQueryResult?.paging.current_page}
          isLoading={isLoading}
        />
      </div>
      {tableQueryResult?.paging  && tableQueryResult.paging.total_page > 1 ? (
        <div className="mt-6">
          <Pagination 
            hideTotal
            currentPage={tableQueryResult?.paging.current_page}
            totalPage={tableQueryResult?.paging.total_page}
            total={{
              totalItems: tableQueryResult?.paging.total_item || 0,
              total: tableQueryResult?.paging.total
            }}
            onChange={(page: number) => _handleFilterChange("page", page)}
          />
        </div>
      ) : <></>}
    </div>

  )
}